import {
    AtSign,
    Bot,
    Building2,
    SlidersHorizontal,
    Sparkles,
    UserRound,
    Workflow,
    Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { PickerGroupHeading } from '@/components/shared/picker-group-heading';
import { cn } from '@/lib/utils';

import type { IVariableItem, VariableIconName } from './types';
import { ITEM_ICONS, resolveVariableItemIconName } from './variable-item-icon';
import { variableTint } from './variable-tints';

/**
 * One glyph per variable CONCEPT — the thing a whole group is about.
 * Deliberately a closed map rather than a free-form lucide name: a producer
 * choosing its own icon is how two pickers end up disagreeing.
 *
 * The glyph for an individual variable is a different question, answered by
 * `variable-item-icon.ts`.
 */
const ICONS: Record<VariableIconName, LucideIcon> = {
    contact: UserRound,
    organization: Building2,
    custom: SlidersHorizontal,
    trigger: Zap,
    step: Workflow,
    ai: Bot,
    extracted: Sparkles,
    variable: AtSign,
};

export function VariableIcon({ name, className }: { name?: VariableIconName; className?: string }) {
    const Icon = ICONS[name ?? 'variable'];
    return <Icon className={className} aria-hidden="true" />;
}

/**
 * The glyph for ONE variable, tinted by the category it sits under.
 *
 * The map lookup happens HERE rather than a resolver handing back a component:
 * a component type must be statically reachable (`react-hooks/static-components`),
 * and the value returned by a plain function call is not.
 */
export function VariableItemIcon({
    item,
    groupIcon,
    className,
}: {
    readonly item: IVariableItem;
    readonly groupIcon?: VariableIconName;
    readonly className?: string;
}) {
    const Icon = ITEM_ICONS[resolveVariableItemIconName(item)];
    return <Icon className={cn(className, variableTint(groupIcon))} aria-hidden="true" />;
}

/**
 * A category heading for a variable picker: the shared picker heading plus this
 * category's tinted glyph.
 *
 * Loud on purpose. The previous treatment (`text-xs font-medium` grey) sat at
 * the same visual weight as the rows it was meant to separate, which is why the
 * list read as one flat run.
 */
export function VariableGroupHeading({
    label,
    icon,
    className,
}: {
    readonly label: string;
    readonly icon?: VariableIconName;
    readonly className?: string;
}) {
    return (
        <PickerGroupHeading
            label={label}
            className={className}
            icon={
                <VariableIcon name={icon} className={cn('size-3.5 shrink-0', variableTint(icon))} />
            }
        />
    );
}
